import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Minus, Trash2, ShoppingCart, CreditCard, Gift, CalendarDays, Baby, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { RewardProgress } from "@/components/RewardProgress";

const configSummary = (config) =>
  config ? Object.values(config).filter(Boolean).join(" · ") : null;

const fmtLong = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
  } catch (e) {
    return iso;
  }
};

export default function CartPage() {
  const navigate = useNavigate();
  const { items, setQuantity, removeItem, preview, previewing } = useCart();
  const [paying, setPaying] = useState(false);
  const [avail, setAvail] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [kids, setKids] = useState([]);
  const [schools, setSchools] = useState({});
  const [childId, setChildId] = useState("");
  const [showReview, setShowReview] = useState(false);

  const loadAvailability = () =>
    api.get("/clover/delivery-availability").then((r) => setAvail(r.data)).catch(() => {});

  useEffect(() => {
    loadAvailability();
    api.get("/children").then(async (r) => {
      const list = r.data.children || [];
      setKids(list);
      const entries = await Promise.all(
        list.map((k) => api.get(`/clover/child-school/${k.child_id}`).then((x) => [k.child_id, x.data]).catch(() => [k.child_id, null]))
      );
      setSchools(Object.fromEntries(entries));
    }).catch(() => {});
  }, []);

  const child = kids.find((k) => k.child_id === childId);
  const childSchool = schools[childId]?.label;

  const openReview = () => {
    if (!childId) { toast.error("Please choose which child this order is for."); return; }
    if (!childSchool) { toast.error("This child has no school set. Add it in My Kids first."); return; }
    if (!deliveryDate) { toast.error("Please choose a delivery date."); return; }
    setShowReview(true);
  };

  const doCheckout = async () => {
    setPaying(true);
    try {
      const payload = {
        items: items.map((i) => ({
          meal_id: i.meal_id,
          quantity: i.quantity,
          ...(i.config ? { config: i.config } : {}),
          ...(i.addons && i.addons.length ? { addons: i.addons } : {}),
        })),
        child_id: childId,
        child_name: child?.name,
        delivery_date: deliveryDate,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/clover/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout.");
      await loadAvailability();
      setShowReview(false);
      setPaying(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <div className="text-6xl mb-4">🛒</div>
        <h1 className="font-display font-bold text-3xl text-slate-800">Your cart is empty</h1>
        <p className="text-slate-500 mt-2">Add some yummy lunches to get started!</p>
        <button
          data-testid="cart-browse-menu"
          onClick={() => navigate("/menu")}
          className="mt-6 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-display font-bold rounded-2xl px-6 py-3 shadow-md shadow-emerald-200"
        >
          Browse the Menu
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-800 tracking-tight flex items-center gap-3">
        <ShoppingCart className="text-emerald-500" /> Your Cart
      </h1>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Items */}
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => {
            const line = preview?.items?.find(
              (p) => p.meal_id === i.meal_id && JSON.stringify(p.config || null) === JSON.stringify(i.config || null)
            );
            const unit = line?.unit_amount ?? i.price_cents;
            return (
              <div
                key={i.key}
                data-testid={`cart-item-${i.key}`}
                className="bg-white rounded-3xl border-2 border-emerald-100/80 p-4 flex gap-4 items-center"
              >
                <img src={i.image_url} alt={i.name} className="h-20 w-20 rounded-2xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-semibold text-lg text-slate-800 truncate">{i.name}</h3>
                  {configSummary(i.config) && (
                    <p className="text-sm text-slate-500 truncate">{configSummary(i.config)}</p>
                  )}
                  <p className="font-semibold text-emerald-600 mt-1">{formatPrice(unit)}</p>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 rounded-2xl p-1.5">
                  <button
                    data-testid={`cart-minus-${i.key}`}
                    onClick={() => setQuantity(i.key, i.quantity - 1)}
                    className="h-8 w-8 rounded-xl bg-white border border-slate-200 text-slate-600 flex items-center justify-center hover:bg-emerald-50"
                  >
                    <Minus size={15} strokeWidth={3} />
                  </button>
                  <span data-testid={`cart-qty-${i.key}`} className="font-display font-bold w-6 text-center">
                    {i.quantity}
                  </span>
                  <button
                    data-testid={`cart-plus-${i.key}`}
                    onClick={() => setQuantity(i.key, i.quantity + 1)}
                    className="h-8 w-8 rounded-xl bg-white border border-slate-200 text-slate-600 flex items-center justify-center hover:bg-emerald-50"
                  >
                    <Plus size={15} strokeWidth={3} />
                  </button>
                </div>
                <button
                  data-testid={`cart-remove-${i.key}`}
                  onClick={() => removeItem(i.key)}
                  className="h-9 w-9 rounded-xl text-rose-400 hover:bg-rose-50 flex items-center justify-center"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="space-y-4">
          <RewardProgress totalQty={preview?.total_qty ?? 0} freeCount={preview?.free_count ?? 0} />

          <div className="bg-white rounded-3xl border-2 border-emerald-100 p-6 sticky top-20">
            <h2 className="font-display font-bold text-xl text-slate-800 mb-4">Order Summary</h2>
            <div className="space-y-3 text-slate-600 font-medium">
              <Row label="Subtotal" value={formatPrice(preview?.subtotal_cents)} testid="summary-subtotal" />
              {preview?.discount_cents > 0 && (
                <div className="flex items-center justify-between text-emerald-600 font-semibold" data-testid="summary-discount">
                  <span className="inline-flex items-center gap-1.5">
                    <Gift size={16} /> Free meal{preview.free_count > 1 ? "s" : ""} ({preview.free_count})
                  </span>
                  <span>−{formatPrice(preview.discount_cents)}</span>
                </div>
              )}
              <div className="h-px bg-slate-200 my-1" />
              <div className="flex items-center justify-between text-slate-900">
                <span className="font-display font-bold text-lg">Total</span>
                <span data-testid="summary-total" className="font-display font-bold text-2xl text-emerald-600">
                  {formatPrice(preview?.total_cents)}
                </span>
              </div>
            </div>

            <div className="mt-5" data-testid="child-block">
              <label className="flex items-center gap-2 font-display font-bold text-slate-800 mb-1.5">
                <Baby size={18} className="text-purple-500" /> Who is this for?
              </label>
              <select
                data-testid="child-select"
                value={childId}
                onChange={(e) => setChildId(e.target.value)}
                className="w-full rounded-2xl border-2 border-slate-200 px-4 py-2.5 text-slate-800 focus:border-emerald-400 focus:outline-none bg-white"
              >
                <option value="">Select a child…</option>
                {kids.map((k) => (
                  <option key={k.child_id} value={k.child_id}>{k.name}</option>
                ))}
              </select>
              {childId && (childSchool
                ? <p data-testid="child-school-line" className="text-sm text-emerald-700 mt-1.5 font-medium">🏫 {childSchool}</p>
                : <p className="text-sm text-amber-600 mt-1.5">No school set — add it in <button type="button" className="underline" onClick={() => navigate("/children")}>My Kids</button>.</p>)}
            </div>

            <div className="mt-4" data-testid="delivery-date-block">
              <label className="flex items-center gap-2 font-display font-bold text-slate-800 mb-1.5">
                <CalendarDays size={18} className="text-emerald-500" /> Delivery date
              </label>
              <input
                type="date"
                data-testid="delivery-date-input"
                value={deliveryDate}
                min={avail?.earliest_date}
                max={avail?.latest_date}
                onChange={(e) => setDeliveryDate(e.target.value)}
                className="w-full rounded-2xl border-2 border-slate-200 px-4 py-2.5 text-slate-800 focus:border-emerald-400 focus:outline-none"
              />
              {deliveryDate ? (
                <p data-testid="delivery-date-chosen" className="text-sm text-emerald-700 mt-1.5 font-medium">
                  Delivering {fmtLong(deliveryDate)}
                </p>
              ) : (
                <p className="text-xs text-slate-400 mt-1.5">
                  Order by <strong>9:00 PM (Edmonton time)</strong> for next-day delivery.
                </p>
              )}
            </div>

            <button
              data-testid="pay-button"
              onClick={openReview}
              disabled={paying || previewing || !preview || !deliveryDate || !childId}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-display font-bold text-lg rounded-2xl py-3.5 shadow-md shadow-emerald-200 transition-all active:scale-[0.98]"
            >
              <CreditCard size={20} /> Review &amp; Pay
            </button>
            <p className="text-xs text-slate-400 text-center mt-3">
              Secure checkout powered by Clover.
            </p>
          </div>
        </div>
      </div>

      {showReview && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setShowReview(false)}>
          <div data-testid="review-modal" className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-2xl text-slate-800">Review your order</h3>
              <button data-testid="review-close" onClick={() => setShowReview(false)} className="text-slate-400 hover:text-slate-600"><X size={22} /></button>
            </div>
            <div className="text-sm text-slate-600 space-y-1">
              <p><strong className="text-slate-800">Child:</strong> {child?.name}</p>
              <p><strong className="text-slate-800">School:</strong> {childSchool}</p>
              <p><strong className="text-slate-800">Delivery date:</strong> {deliveryDate}</p>
            </div>
            <div className="border-t-2 border-slate-100 pt-3 space-y-2">
              {(preview?.items || items).map((it, idx) => (
                <div key={idx} data-testid={`review-item-${idx}`} className="text-sm">
                  <div className="flex justify-between text-slate-700">
                    <span>{it.name} × {it.quantity}</span>
                    <span>{formatPrice(it.unit_amount)}</span>
                  </div>
                  {(it.addons || []).length > 0 && (
                    <p className="text-xs text-emerald-600 pl-2">+ {it.addons.join(", ")}</p>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t-2 border-slate-100 pt-3 space-y-1 text-sm">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{formatPrice(preview?.subtotal_cents)}</span></div>
              {preview?.addon_total_cents > 0 && (
                <div className="flex justify-between text-slate-600"><span>Add-ons</span><span>+{formatPrice(preview.addon_total_cents)}</span></div>
              )}
              {preview?.discount_cents > 0 && (
                <div className="flex justify-between text-emerald-600 font-semibold"><span>Free meal ({preview.free_count})</span><span>−{formatPrice(preview.discount_cents)}</span></div>
              )}
              <div className="flex justify-between font-display font-bold text-lg text-slate-900"><span>Total</span><span data-testid="review-total" className="text-emerald-600">{formatPrice(preview?.total_cents)}</span></div>
            </div>
            <div className="flex gap-3 pt-2">
              <button data-testid="review-back" onClick={() => setShowReview(false)} className="flex-1 rounded-2xl border-2 border-slate-200 text-slate-600 font-semibold py-3">Go back</button>
              <button data-testid="review-confirm-pay" onClick={doCheckout} disabled={paying} className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-display font-bold rounded-2xl py-3">
                <CreditCard size={18} /> {paying ? "Redirecting…" : `Pay ${formatPrice(preview?.total_cents)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between">
    <span>{label}</span>
    <span data-testid={testid} className="font-semibold text-slate-800">
      {value}
    </span>
  </div>
);
